import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
export function PricingPreview() {
  return <div className="py-24 bg-gate-dark" data-id="element-239">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-240">
        <div className="text-center mb-16" data-id="element-241">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-242">
            Hardware Comparison
          </h2>
          <p className="text-gate-text-secondary max-w-2xl mx-auto" data-id="element-243">
            See why FPGAs are the superior choice for inference workloads.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-244">
          {/* CPU Card */}
          <div className="p-8 rounded-2xl bg-gate-surface border border-gate-border opacity-70 hover:opacity-100 transition-opacity" data-id="element-245">
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-246">CPU Inference</h3>
            <p className="text-sm text-gate-text-muted mb-6" data-id="element-247">
              Standard x86/ARM Processors
            </p>
            <div className="space-y-4 mb-8" data-id="element-248">
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-249">
                <span className="text-gate-text-secondary" data-id="element-250">Latency</span>
                <span className="text-white font-mono" data-id="element-251">15-50ms</span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-252">
                <span className="text-gate-text-secondary" data-id="element-253">Throughput</span>
                <span className="text-white font-mono" data-id="element-254">Low</span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-255">
                <span className="text-gate-text-secondary" data-id="element-256">Power</span>
                <span className="text-white font-mono" data-id="element-257">100W+</span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-258">
                <span className="text-gate-text-secondary" data-id="element-259">Cost / 1M</span>
                <span className="text-white font-mono" data-id="element-260">$0.50+</span>
              </div>
            </div>
          </div>

          {/* FPGA Card (Highlighted) */}
          <div className="relative p-8 rounded-2xl bg-gate-surface border-2 border-gate-teal shadow-[0_0_40px_-10px_rgba(0,212,170,0.2)] transform md:-translate-y-4" data-id="element-261">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gate-teal text-gate-dark px-4 py-1 rounded-full text-sm font-bold shadow-lg" data-id="element-262">
              Recommended
            </div>
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-263">
              FPGA Inference
            </h3>
            <p className="text-sm text-gate-text-muted mb-6" data-id="element-264">
              OpenFPGA Optimized Hardware
            </p>
            <div className="space-y-4 mb-8" data-id="element-265">
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-266">
                <span className="text-gate-text-secondary" data-id="element-267">Latency</span>
                <span className="text-gate-teal font-bold font-mono" data-id="element-268">
                  {'<'}1ms
                </span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-269">
                <span className="text-gate-text-secondary" data-id="element-270">Throughput</span>
                <span className="text-gate-teal font-bold font-mono" data-id="element-271">High</span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-272">
                <span className="text-gate-text-secondary" data-id="element-273">Power</span>
                <span className="text-gate-teal font-bold font-mono" data-id="element-274">
                  10-30W
                </span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-275">
                <span className="text-gate-text-secondary" data-id="element-276">Cost / 1M</span>
                <span className="text-gate-teal font-bold font-mono" data-id="element-277">
                  $0.02+
                </span>
              </div>
            </div>
            <ul className="space-y-3 mb-8" data-id="element-278">
              <li className="flex items-center text-sm text-white" data-id="element-279">
                <Check className="w-4 h-4 text-gate-teal mr-2" data-id="element-280" />
                Deterministic Timing
              </li>
              <li className="flex items-center text-sm text-white" data-id="element-281">
                <Check className="w-4 h-4 text-gate-teal mr-2" data-id="element-282" />
                Bitstream Hot-Swapping
              </li>
              <li className="flex items-center text-sm text-white" data-id="element-283">
                <Check className="w-4 h-4 text-gate-teal mr-2" data-id="element-284" />
                Custom Logic Support
              </li>
            </ul>
            <a href="https://docs.google.com/forms/d/e/1FAIpQLSeA3akOwONwkYJLK4nX5RaNw1wlVWWLd6dPKvH2FSMPjvA9IA/viewform" target="_blank" rel="noopener noreferrer" className="block w-full py-3 rounded-lg bg-gate-teal text-gate-dark font-bold hover:bg-gate-teal/90 transition-colors text-center" data-id="element-285">
              Start Building
            </a>
          </div>

          {/* GPU Card */}
          <div className="p-8 rounded-2xl bg-gate-surface border border-gate-border opacity-70 hover:opacity-100 transition-opacity" data-id="element-286">
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-287">GPU Inference</h3>
            <p className="text-sm text-gate-text-muted mb-6" data-id="element-288">
              Standard H100/A100 Clusters
            </p>
            <div className="space-y-4 mb-8" data-id="element-289">
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-290">
                <span className="text-gate-text-secondary" data-id="element-291">Latency</span>
                <span className="text-white font-mono" data-id="element-292">10-100ms</span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-293">
                <span className="text-gate-text-secondary" data-id="element-294">Throughput</span>
                <span className="text-white font-mono" data-id="element-295">Very High</span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-296">
                <span className="text-gate-text-secondary" data-id="element-297">Power</span>
                <span className="text-white font-mono" data-id="element-298">300W+</span>
              </div>
              <div className="flex justify-between border-b border-gate-border pb-2" data-id="element-299">
                <span className="text-gate-text-secondary" data-id="element-300">Cost / 1M</span>
                <span className="text-white font-mono" data-id="element-301">$0.10+</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-302">
          <a href="#" className="inline-flex items-center text-gate-teal hover:text-gate-teal/80 font-medium transition-colors" data-id="element-303">
            View Full Pricing <ArrowRight className="ml-2 w-4 h-4" data-id="element-304" />
          </a>
        </div>
      </div>
    </div>;
}